import type { ButtonProps } from '@mui/material';
import { Box, Divider, FormControlLabel, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import isEqual from 'lodash.isequal';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Button, Checkbox } from '@/shared/ui';
import type { AccountInfo, ActiveStatusRecord } from '../../model/types';

const ChangesButton = styled((props: ButtonProps) => {
  return <Button variant="outlined" color="secondary" {...props} />;
})(({ theme }) => ({
  lineHeight: '1.125rem',
  padding: `${theme.spacing(1.25)} ${theme.spacing(2)}`,
  borderRadius: theme.shape.borderRadius * 2,
}));

interface Props {
  LabelComponent: FC<{ accountInfo: AccountInfo }>;
  allAcounts: AccountInfo[];
  value: ActiveStatusRecord;
  onChange: (v: ActiveStatusRecord) => void;
}

export function AccountsActiveStatusList({
  LabelComponent,
  allAcounts,
  value,
  onChange,
}: Props) {
  const [internalValue, setInternalValue] = useState(value);
  const [moreExpanded, setMoreExpanded] = useState(false);
  const hasUnsavedChanges = !isEqual(value, internalValue);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  if (allAcounts.length === 0) {
    return (
      <Box sx={{ p: 2, pt: 0 }}>
        <Typography color="secondary" variant="caption">
          No ad accounts linked.
        </Typography>
      </Box>
    );
  }

  const handleItemChange =
    (id: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked;

      if (newValue) {
        setInternalValue({
          ...internalValue,
          [id]: true,
        });
      } else {
        const { [id]: accountToRemove, ...newInternalValue } = internalValue;

        setInternalValue(newInternalValue);
      }
    };
  const handleResetChange = () => {
    setInternalValue(value);
  };
  const handleSubmitChange = () => {
    onChange(internalValue);
  };
  const toggleMoreExpanded = () => {
    setMoreExpanded(!moreExpanded);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2 }}>
        <Typography sx={{ color: '#7E828F', fontSize: '0.875rem' }}>
          Ad accounts list:
        </Typography>
      </Box>
      <Box
        component="ul"
        sx={{
          p: 2,
          margin: 0,
          listStyle: 'none',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 2,
        }}
      >
        {allAcounts
          .slice(0, moreExpanded ? allAcounts.length : 4)
          .map((account) => {
            return (
              <li key={account.id}>
                <FormControlLabel
                  sx={{
                    m: 0,
                    gap: 1,
                    alignItems: 'flex-start',
                    fontSize: '0.875rem',
                    lineHeight: '1.125rem',
                  }}
                  disableTypography
                  label={<LabelComponent accountInfo={account} />}
                  control={
                    <Checkbox
                      sx={{
                        height: 18,
                        width: 18,
                      }}
                      checked={internalValue[account.id] || false}
                      onChange={handleItemChange(account.id)}
                    />
                  }
                />
              </li>
            );
          })}
      </Box>
      {allAcounts.length > 4 && (
        <Button
          sx={{
            alignSelf: 'flex-start',
            py: 0,
            px: 0.5,
            ml: 1.5,
            mb: 2,
            lineHeight: '1.125rem',
            minWidth: 0,
          }}
          variant="text"
          onClick={toggleMoreExpanded}
        >
          {!moreExpanded ? 'Show More' : 'Hide'}
        </Button>
      )}
      {hasUnsavedChanges && (
        <>
          <Divider />
          <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
            <ChangesButton
              variant="contained"
              color="primary"
              onClick={handleSubmitChange}
            >
              Update
            </ChangesButton>
            <ChangesButton onClick={handleResetChange}>Cancel</ChangesButton>
          </Box>
        </>
      )}
    </Box>
  );
}
